import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { assert } from 'src/app/core/assert/assert';
import { AuthService } from '../../../auth/auth.service';
import { TimeBlockModel } from '../../../core/models/timeblock/time-block.model';
import { TrackerResponseModel } from '../../../core/models/tracker/tracker-response.model';
import { ShiveDialogService } from '../../../core/services/controls/shive-dialog.service';
import { SharedClockHandlerService } from '../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../core/services/features/time-tracking/shared-timer-handler.service';
import {
  SharedTrackerHandlerService,
  TrackerStatus,
} from '../../../core/services/features/time-tracking/shared-tracker-handler.service';
import { TimerStatus } from '../../../features/calendar/calendar-types';
import { SharedActiveTimeBlockService } from '../../../features/calendar/components/time-block/active/shared-active-time-block.service';
import { TimeBlockItemBuilderService } from '../../../features/calendar/components/time-block/generation/time-block-item-builder.service';
import {
  GeneratedTimeBlockComponent,
  ITimeBlockComponentItem,
} from '../../../features/calendar/components/time-block/types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockType,
} from '../../../features/calendar/components/time-block/types/time-block-types';
import { TimeEntryDialogComponent } from '../../../features/calendar/dialogs/time-entry-dialog/time-entry-dialog.component';
import { CalendarService } from '../../../features/calendar/services/calendar.service';
import { DateTimeHelper } from '../../../features/calendar/util/date-time-helper';
import { TimeBlockSource } from '../../../shared/data-types/shared-time-block-types';
import { TimeEntryConverter } from './converter/time-entry.converter';
import { timeEntryListTrackerStarted } from './state-and-data-handling/time-entries-data.actions';
import { TimeEntriesStore } from './state-and-data-handling/time-entries-data.store';
import { TimeEntriesListViewHttpService } from './time-entries-list-view-http.service';
import { TimeBlockListEntry } from './time-enty-list-types';

@Injectable()
export class TimeEntriesListViewService {
  public readonly trackerActive = signal<null | boolean>(null);
  public readonly trackerStatus = signal<TrackerStatus>(TrackerStatus.None);
  public readonly timerActive = signal<null | boolean>(null);

  public readonly startTimerButton = computed(() => [
    {
      text: 'Timer starten...',
      click: (): void => this.toggleTimer(),
      disabled: !!this.timerActive() || !!this.trackerActive(),
    },
  ]);
  public trackerSub$ = null;

  // DI
  public readonly globalStore = inject(Store);
  public readonly timeEntriesStore = inject(TimeEntriesStore);
  private readonly sharedTrackerHandlerService = inject(SharedTrackerHandlerService);
  private readonly sharedTimerHandlerService = inject(SharedTimerHandlerService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly timeEntriesListViewHttpService = inject(TimeEntriesListViewHttpService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly calendarService = inject(CalendarService);
  private readonly sharedClockHandlerService = inject(SharedClockHandlerService);
  private readonly authService = inject(AuthService);

  // Misc
  private readonly activeTimerTimeBlock = toSignal(this.timeEntriesStore.activeTimerTimeBlock$, {
    initialValue: null,
  });

  constructor() {
    effect(() => {
      const block = this.activeTimerTimeBlock();
      this.sharedTimerHandlerService.timerActiveTBChanged$.next(
        block?.id ?? SharedActiveTimeBlockService.noActiveTimerTimeBlockId,
      );
      this.timerActive.set(!!block);
    });
  }

  public readonly isRowSelectable = (args: { dataItem: any; index: number }) => {
    return !args.dataItem?.isActive;
  };

  public initActiveTimeBlockTracking(): void {
    this.trackerSub$ = this.sharedTrackerHandlerService.trackerResponse$
      .pipe(filter((response) => response !== null))
      .subscribe((trackerResponse) => {
        this.processTrackerResponse(trackerResponse);
      });
  }

  public openDialog(timeEntry?: TimeBlockListEntry): void {
    let timeBlock;

    if (!timeEntry) {
      const start = new Date();
      let end = new Date(start);
      end = DateTimeHelper.addMinutes(
        end,
        this.calendarService.model.calendarProperties.minimumTimeBlockDuration,
      );

      const timeBlockModel = new TimeBlockModel();
      timeBlock = new GeneratedTimeBlockComponent(timeBlockModel, TimeBlockContentType.Project);
      timeBlock.timeBlockModel.start = start;
      timeBlock.timeBlockModel.end = end;
      timeBlock.timeBlockModel.fixInterval = null;
      timeBlock.timeBlockModel.userId = this.authService.getLoggedInUserClone().id;
      timeBlock.timeBlockModel.type = TimeBlockType.NonExistingBlock;
    } else {
      timeBlock = this.timeBlockItemBuilderService.buildFromExisting(
        timeEntry.originalTimeBlockModel,
        timeEntry.contentType,
      );
    }

    const dialogRef = this.shiveDialogService.open({
      cssClass: 'time-entry-dialog-wrapper',
      content: TimeEntryDialogComponent,
    });

    const timeEntryDialogComponent = dialogRef.content.instance as TimeEntryDialogComponent;
    timeEntryDialogComponent.selectedTimeBlockComponent.update(() => timeBlock);
  }

  public addToStore(timeBlock: ITimeBlockComponentItem): void {
    const timeEntry = TimeEntryConverter.toTimeBlockListEntry(timeBlock);
    this.timeEntriesStore.addSortedTimeBlockEntry(timeEntry);
  }

  public replaceInStore(original: ITimeBlockComponentItem, updated: ITimeBlockComponentItem): void {
    const originalTimeEntry = TimeEntryConverter.toTimeBlockListEntry(original);
    const updatedTimeEntry = TimeEntryConverter.toTimeBlockListEntry(updated);
    this.timeEntriesStore.replaceTimeBlockEntry({
      original: originalTimeEntry,
      updated: updatedTimeEntry,
    });

    // If active, replace active timer or tracker time block.
    if (updated.timeBlockModel.isActive) {
      // Update clock start with current start time of time block. Needed if time block start changes via dialog.
      this.sharedClockHandlerService.setClockStart = updated.timeBlockModel.start.getTime();
      const isTimer = updated.timeBlockModel.source === TimeBlockSource.TimerSource;
      if (isTimer) {
        this.timeEntriesStore.setActiveTimerTimeBlockEntry(updated);
        return;
      }
      this.timeEntriesStore.setActiveTrackerTimeBlockEntry(updated);
    }
  }

  public deleteInStore(deletedId: number): void {
    this.timeEntriesStore.deleteInStore(deletedId);
  }

  public updateActiveTimeBlockEndDate(): void {
    const activeClock$ = this.sharedClockHandlerService.activeClockChanged$;
    const activeTimerTimeBlock = this.timeEntriesStore.activeTimerTimeBlock$;
    const activeTrackerTimeBlock$ = this.timeEntriesStore.activeTrackerTimeBlock$;

    combineLatest([activeClock$, activeTimerTimeBlock, activeTrackerTimeBlock$])
      .pipe()
      .subscribe(([, activeTimerTimeBlock, activeTrackerTimeBlock]) => {
        if (!activeTimerTimeBlock && !activeTrackerTimeBlock) {
          return;
        }

        let start = null;
        let end = null;
        let activeTimeBlock: ITimeBlockComponentItem | null = null;
        const elapsedSeconds = this.sharedClockHandlerService.getCurrentTimerSeconds();

        if (activeTrackerTimeBlock) {
          start = activeTrackerTimeBlock.timeBlockModel.start;
          end = activeTrackerTimeBlock.timeBlockModel.end;
          activeTimeBlock = activeTrackerTimeBlock;
        }

        if (activeTimerTimeBlock) {
          start = activeTimerTimeBlock.timeBlockModel.start;
          end = activeTimerTimeBlock.timeBlockModel.end;
          activeTimeBlock = activeTimerTimeBlock;
        }

        const minimumTimeBlockDuration =
          this.calendarService.model.calendarProperties.minimumTimeBlockDuration;

        start = DateTimeHelper.setSecondsAndMillisToZero(start);
        const diff = DateTimeHelper.minutesToSeconds(minimumTimeBlockDuration) - elapsedSeconds;

        if (diff < 0) {
          end = DateTimeHelper.addSeconds(start, elapsedSeconds);
        }

        // If we are within the minimum time block duration range, use the end time.
        // Zero the seconds here so that in the time block dialog, end time is consistent.
        end = DateTimeHelper.setSecondsAndMillisToZero(end);

        const clonedTimeBlock = cloneDeep(activeTimeBlock);
        clonedTimeBlock.timeBlockModel.start = start;
        clonedTimeBlock.timeBlockModel.end = end;

        const originalEntry = TimeEntryConverter.toTimeBlockListEntry(activeTimeBlock);
        const clonedEntry = TimeEntryConverter.toTimeBlockListEntry(clonedTimeBlock);

        this.timeEntriesStore.replaceTimeBlockEntry({
          original: originalEntry,
          updated: clonedEntry,
        });
      });
  }

  // /######### Timer ########///
  public toggleTimer() {
    this.sharedTimerHandlerService.toggleTimer();
  }

  // /######### Tracker ########///
  private startTracker(timeBlockModel: TimeBlockModel): void {
    // After the tracker starts, check if a timer time block is active.
    const activeTimerTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
    if (activeTimerTimeBlockId !== SharedActiveTimeBlockService.noActiveTimerTimeBlockId) {
      // A timer time block is active, so stop searching it in the time block scet and set it to active = false.
      const allEntries = this.timeEntriesStore.state().entries;
      const entry = allEntries.find((entry) => entry.id === activeTimerTimeBlockId);
      assert(entry.isActive && entry.source === TimeBlockSource.TimerSource);

      const updatedEntry = { ...entry };
      updatedEntry.isActive = false;

      // Reset clock start.
      this.sharedClockHandlerService.setClockStart = timeBlockModel.start.getTime();

      // Send the updated timer time block to the data store and replace it there.
      this.globalStore.dispatch(
        timeEntryListTrackerStarted({
          payload: {
            status: TimerStatus.Stop,
            activeTimerEntry: updatedEntry,
          },
        }),
      );
    }

    const activeTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      timeBlockModel,
      TimeBlockContentType.Project,
    );

    // Insert active time entry to general store entry list.
    this.addToStore(activeTimeBlock);

    // Keep tracker entry as separate object in store to make selection easier.
    this.timeEntriesStore.setActiveTrackerTimeBlockEntry(activeTimeBlock);

    this.sharedTrackerHandlerService.trackerTimeBlockComponent$.next(
      activeTimeBlock.timeBlockModel,
    );
  }

  private stopTracker(): void {
    const activeTrackerTimeBlock =
      this.sharedTrackerHandlerService.trackerTimeBlockComponent$.value;

    if (!activeTrackerTimeBlock) {
      return;
    }

    this.timeEntriesStore.setActiveTrackerTimeBlockEntry(null);
    this.sharedTrackerHandlerService.trackerTimeBlockComponent$.next(null);
    this.timeEntriesListViewHttpService.loadTimeBlocks().pipe(take(1)).subscribe();
  }

  private updateTimeBlock(original: TimeBlockModel, updated: TimeBlockModel): void {
    const originalTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(original);
    const updatedTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(updated);

    // Update tracker time block in store.
    this.replaceInStore(originalTimeBlock, updatedTimeBlock);
    this.timeEntriesStore.setActiveTrackerTimeBlockEntry(updatedTimeBlock);
    this.sharedTrackerHandlerService.trackerTimeBlockComponent$.next(updated);
  }

  private processTrackerResponse(trackerResponseModel: TrackerResponseModel): void {
    const trackerTimeBlockModel = trackerResponseModel.timeBlockModel;

    this.trackerActive.set(trackerResponseModel.isTrackerAlive);

    const originalTimeBlockModel =
      this.sharedTrackerHandlerService.trackerTimeBlockComponent$.value;
    const needsTimeBlockRender = trackerTimeBlockModel && !originalTimeBlockModel;

    const trackerStatus = this.sharedTrackerHandlerService.trackerStatus$.value;
    if (needsTimeBlockRender) {
      this.startTracker(trackerTimeBlockModel);
    } else if (trackerStatus === TrackerStatus.Pause || trackerStatus === TrackerStatus.None) {
      this.stopTracker();
    } else if (
      this.sharedTrackerHandlerService.timeBlockSettingsChanged(
        trackerTimeBlockModel,
        originalTimeBlockModel,
      )
    ) {
      this.updateTimeBlock(originalTimeBlockModel, trackerTimeBlockModel);
    }

    this.trackerStatus.set(trackerStatus);
  }
}
